import React from "react";
import { Check } from "lucide-react";

/**
 * Two-line row inside the session-switcher dropdown. Top line: event
 * name (flexible) plus right-aligned date. Second line spans full width
 * for venue / distinct-session-name secondary detail. Keeps long event
 * names from being crushed against the right column.
 *
 * Plus the small SectionHeader / Divider helpers used to break up the
 * dropdown into "Current" / "Upcoming" / "Results" sections.
 */
export function SessionRow({
  eventName,
  dateLabel,
  secondary,
  isCurrent = false,
  onSelect,
}: {
  eventName: string;
  dateLabel: string;
  secondary?: string | null;
  isCurrent?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      className="flex items-start w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
      onClick={onSelect}
      type="button"
    >
      <div className="flex-shrink-0 w-5 mr-2 mt-0.5">
        {isCurrent && (
          <Check className="h-4 w-4 text-gray-900 dark:text-white" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {eventName}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 whitespace-nowrap">
            {dateLabel}
          </span>
        </div>
        {secondary && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
            {secondary}
          </p>
        )}
      </div>
    </button>
  );
}

export function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-2 pb-1 text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
      {children}
    </div>
  );
}

export function Divider() {
  return (
    <div className="my-1 border-t border-gray-100 dark:border-slate-700/50" />
  );
}
